import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Heart, TrendingUp, Clock, Users, Brain, Sparkles, Map } from 'lucide-react';
import { CaseStudyHero } from '../components/CaseStudyHero';
import { CaseStudyContent } from '../components/CaseStudyContent';
import { CaseStudySection } from '../components/CaseStudySection';
import { MetricCard } from '../components/MetricCard';
import { ImageGallery } from '../components/ImageGallery';
import { Footer } from '../components/Footer';
import { FeatureModal } from '../components/FeatureModal';
import { motion } from 'framer-motion';
import { CaseStudyTableOfContents } from '../components/CaseStudyTableOfContents';
const features = [{
  id: 1,
  title: 'Emotional Processing with Affi',
  description: 'A guided, conversational experience that helps users slow down, reflect, and work through emotional pain in moments of high activation.',
  image: 'https://cdn.magicpatterns.com/uploads/r6m2SaMHMc6QVupHdXY9s9/affi.png',
  modalImage: 'https://cdn.magicpatterns.com/uploads/9HkrwtKE3UTvxsAoHC3otB/innermap_conversation.png',
  userInsight: "People in emotional distress often don't know how to articulate what they're feeling, and generic chatbots either validate harmful thought patterns or offer surface-level platitudes that feel hollow.",
  solution: 'An empathic AI companion, Affi, trained on EFT principles to guide users through deep emotional exploration — asking the right questions, holding space, and never validating the invalid.',
  userOutcome: 'Users report feeling genuinely heard and supported, with a structured path through difficult emotions rather than being left to spiral alone.',
  businessOutcome: 'Beta users demonstrate 8+ month retention, ending therapy because their symptoms remitted — not because they quit.',
  quote: 'For the first time, I felt like something actually understood what I was going through without just telling me everything would be okay.'
}, {
  id: 2,
  title: 'Mapping our emotions',
  description: "A living visualization of a user's emotional world: mapping particular experiences, patterns, and root causes over time into a personal emotional landscape.",
  image: 'https://cdn.magicpatterns.com/uploads/aYUuTfkAwiNHT5CnXzMn6W/affi-laptop.png',
  modalImage: 'https://cdn.magicpatterns.com/uploads/tDUsxfyTnvsTjZDU8FhGGw/innermap_atlas_1.png',
  userInsight: "Emotional growth is hard to see in the moment. Users often feel stuck or like they're going in circles, unable to connect current struggles to deeper patterns.",
  solution: "A dynamic, visual map of the user's emotional world that grows with each session — surfacing connections between experiences, identifying recurring themes, and revealing root causes.",
  userOutcome: 'Users develop a rich self-awareness, learning to think about their inner world the way a psychologist would — with curiosity and pattern recognition rather than judgment.',
  businessOutcome: 'The innermap becomes a compelling reason to stay engaged with the platform, as it grows more valuable the longer users interact with it.',
  quote: "Seeing my patterns laid out visually made me realize things about myself I'd never been able to articulate before."
}, {
  id: 3,
  title: 'Therapist Dashboard',
  description: "A purpose-built tool giving therapists insight into their patients' emotional experiences between sessions, enabling more targeted and effective in-session work.",
  image: '',
  modalImage: 'https://cdn.magicpatterns.com/uploads/fPihviHNdRGgLLfRvHaxH9/innermap-dashboard.png',
  isWip: false,
  userInsight: 'Therapists spend the first portion of every session catching up on what happened since the last appointment. This limits the time available for deep therapeutic work.',
  solution: "A dashboard that surfaces key emotional events, patterns, and progress from a patient's between-session activity — giving therapists a head start before the session even begins.",
  userOutcome: 'Therapists arrive at sessions already oriented, allowing them to skip the catch-up and dive directly into meaningful work.',
  userOutcomeLabel: 'Anticipated user outcome:',
  businessOutcome: 'Real progress in session in half the time, with beta therapy users demonstrating superior retention and stickiness compared to competitors.',
  businessOutcomeLabel: 'Anticipated business outcome:',
  quote: ''
}];
const sections = [{
  id: 'overview',
  title: 'Overview',
  description: 'An empathic AI for the inner world'
}, {
  id: 'challenge',
  title: 'The Challenge',
  description: 'Building AI that truly understands emotion'
}, {
  id: 'team',
  title: 'The Team',
  description: 'A small, mission-driven founding team'
}, {
  id: 'research',
  title: 'Research & Discovery',
  description: 'Understanding emotional needs and therapy gaps'
}, {
  id: 'insights',
  title: 'Key Insights',
  description: 'What users and therapists really need'
}, {
  id: 'ideation',
  title: 'Ideation & Planning',
  description: 'Defining the visual metaphor and system'
}, {
  id: 'solution',
  title: 'The Solution',
  description: 'Affi, the Innermap, and the Therapist Dashboard'
}];
export function InnermapCaseStudy() {
  const [selectedFeature, setSelectedFeature] = useState<(typeof features)[0] | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const openFeature = (feature: (typeof features)[0]) => {
    setSelectedFeature(feature);
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setIsModalOpen(false);
  };
  const goToPrevious = () => {
    if (!selectedFeature) return;
    const currentIndex = features.findIndex(f => f.id === selectedFeature.id);
    if (currentIndex > 0) {
      setSelectedFeature(features[currentIndex - 1]);
    }
  };
  const goToNext = () => {
    if (!selectedFeature) return;
    const currentIndex = features.findIndex(f => f.id === selectedFeature.id);
    if (currentIndex < features.length - 1) {
      setSelectedFeature(features[currentIndex + 1]);
    }
  };
  const currentIndex = selectedFeature ? features.findIndex(f => f.id === selectedFeature.id) : -1;
  return <div className="min-h-screen bg-white font-sans text-slate-900" data-id="element-616">
      <CaseStudyTableOfContents sections={sections} data-id="element-617" />

      {/* Back Button */}
      <Link to="/" className="fixed top-8 left-8 z-50 flex items-center gap-2 rounded-full bg-white/90 backdrop-blur-sm px-4 py-2 text-sm font-medium text-slate-700 shadow-lg transition-all hover:bg-white hover:shadow-xl hover:scale-105" data-id="element-618">
        <ArrowLeft size={16} data-id="element-619" />
        Back to Home
      </Link>

      {/* SECTION 1: HERO IMAGE */}
      <CaseStudyHero image="https://cdn.magicpatterns.com/uploads/95NpE8Tz8BFi7DHuYoRz2a/innermap_hero.png" mobileImage="https://cdn.magicpatterns.com/uploads/tDWE3sKXPrQSuA9E42vaga/innermap_hero_mobile.png" data-id="element-620" />

      {/* SECTION 1.5: HERO CONTENT */}
      <CaseStudyContent title="Mapping the inner world" subtitle="Designing an empathic AI companion and emotional mapping system to help people understand themselves and feel better." role="Fractional Experience Strategist" timeline="2025 – Present" client="Innermap" outcome="Beta launch" data-id="element-621" />

      {/* Stats Bridge Bar */}
      <div className="relative z-10 mx-auto max-w-4xl px-6 -mt-8 mb-[-3rem]" data-id="element-622">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7,
        ease: 'easeOut'
      }} className="bg-white rounded-2xl shadow-xl border border-slate-100 px-6 py-8 md:px-10" data-id="element-623">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-4" data-id="element-624">
            {[{
            value: '<5%',
            label: 'Target dropout rate vs. 30% industry standard',
            icon: TrendingUp
          }, {
            value: '6+',
            label: 'Months target client retention',
            icon: Clock
          }, {
            value: '2×',
            label: 'Faster progress in session',
            icon: Sparkles
          }].map((stat, index) => <motion.div key={stat.label} initial={{
            opacity: 0,
            y: 15
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3 + index * 0.12,
            duration: 0.5
          }} className="flex items-center gap-4 md:justify-center" data-id="element-625">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center" data-id="element-626">
                  <stat.icon className="w-5 h-5 text-blue-600" data-id="element-627" />
                </div>
                <div data-id="element-628">
                  <p className="text-2xl md:text-3xl font-bold text-blue-600 leading-none" data-id="element-629">
                    {stat.value}
                  </p>
                  <p className="text-sm text-slate-500 mt-1 leading-snug" data-id="element-630">
                    {stat.label}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </motion.div>
      </div>

      {/* SECTION 2: OVERVIEW */}
      <CaseStudySection id="overview" title="Overview" subtitle="An empathic AI for the inner world." background="white" data-id="element-631">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2" data-id="element-632">
          <div className="space-y-6 text-lg text-slate-600" data-id="element-633">
            <p data-id="element-634">
              Innermap is a mental health platform built around a simple but
              radical idea: that AI-based technology can help people understand
              themselves more deeply and genuinely feel better over time.
            </p>
            <p data-id="element-635">
              At its core is Affi, an empathic AI companion trained on
              Emotionally Focused Therapy (EFT) principles. Affi guides users
              through difficult emotional experiences in moments of high
              activation, helping them slow down, reflect, and process rather
              than spiral. Over time, the system builds a living map of the
              user's emotional world: their patterns, triggers, and root causes.
            </p>
            <p data-id="element-636">
              I joined Innermap as a fractional Experience Strategist, leading
              the prototyping of both the consumer app and a therapist
              dashboard, while also helping shape the product vision and
              narrative.
            </p>
          </div>
          <div className="flex items-center justify-center rounded-2xl bg-blue-50 p-8" data-id="element-637">
            <div className="space-y-4" data-id="element-638">
              <h3 className="text-xl font-bold text-slate-900" data-id="element-639">
                Key Objectives
              </h3>
              <ul className="space-y-3" data-id="element-640">
                {['Design an empathic, EFT-grounded AI experience', 'Define a compelling visual metaphor for the consumer experience', 'Prototype the consumer app and therapist dashboard', 'Craft the investor pitch and core messaging'].map((item, i) => <li key={i} className="flex items-start gap-3 text-slate-700" data-id="element-641">
                    <div className="mt-1.5 h-2 w-2 rounded-full bg-blue-500 flex-shrink-0" data-id="element-642" />
                    {item}
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </CaseStudySection>

      {/* SECTION 3: CHALLENGE */}
      <CaseStudySection id="challenge" title="The Challenge" subtitle="Building AI that truly understands emotion, without doing harm." background="gray" data-id="element-643">
        <div className="max-w-3xl space-y-6 text-lg text-slate-600" data-id="element-644">
          <p data-id="element-645">
            Most mental health apps treat symptoms at the surface. They offer
            breathing exercises, mood tracking, and generic affirmations, but
            they don't help people understand why they feel the way they do.
          </p>
          <p data-id="element-646">
            Worse, AI companions in this space often make things worse by
            validating harmful thought patterns. Innermap takes this seriously:
            the system is finely tuned to prevent validating the invalid, even
            when it feels hard.
          </p>
          <p data-id="element-647">
            The design challenge was to create an experience that felt warm,
            safe, and genuinely supportive while maintaining the clinical
            integrity that makes it actually therapeutic.
          </p>
        </div>
      </CaseStudySection>

      {/* SECTION 3.5: THE TEAM */}
      <CaseStudySection id="team" title="The Team" subtitle="A small, mission-driven founding team." background="white" data-id="element-648">
        <div className="space-y-12" data-id="element-649">
          <p className="text-lg text-slate-600 leading-relaxed max-w-3xl" data-id="element-650">
            Innermap was built by a tight-knit team of believers: three founding
            therapists and an AI tech lead, plus a tech lead, a product lead,
            and me as the design lead. The small size means everyone wears
            multiple hats and collaboration is constant and direct.
          </p>

          <div className="mt-16 pt-16 border-t border-slate-200" data-id="element-651">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6" data-id="element-652">
              <div className="max-w-2xl" data-id="element-653">
                <h3 className="text-2xl font-bold text-slate-900 mb-3" data-id="element-654">
                  My Approach
                </h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-655">
                  Working as a fractional strategist over one year, I combined
                  collaborative design thinking with a lean, iterative process —
                  moving quickly from concept to prototype while staying closely
                  grounded in clinical expertise and user feedback.
                </p>
              </div>
              <div className="flex flex-wrap justify-center gap-2" data-id="element-656">
                {['Collaborative', 'Clinically-grounded', 'Iterative', 'Experimentative', 'Lean'].map(tag => <span key={tag} className="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 rounded-full border border-blue-200" data-id="element-657">
                    {tag}
                  </span>)}
              </div>
            </div>
          </div>
        </div>
      </CaseStudySection>

      {/* SECTION 4: RESEARCH & DISCOVERY */}
      <CaseStudySection id="research" title="Research & Discovery" subtitle="Understanding what people actually need when they're struggling." activities={['Stakeholder Interviews', 'Therapist Interviews', 'User Interviews', 'Competitive Analysis', 'Clinical Framework Review']} background="gray" data-id="element-658">
        <div className="space-y-6" data-id="element-659">
          <p className="text-lg text-slate-700 leading-relaxed" data-id="element-660">
            Research began with the founding therapists, learning the clinical
            framework deeply before talking to users. I then conducted
            interviews with patients in their practice to understand the moments
            when people most needed support and what existing tools failed to
            provide.
          </p>
          <p className="text-lg text-slate-700 leading-relaxed" data-id="element-661">
            A competitive audit of mental health apps and current usage of AI as
            "therapist" revealed a consistent gap: most tools addressed
            symptoms, not causes. None offered a way to build genuine
            self-understanding over time. And worse, AI-based therapy today can
            have very dangerous outcomes.
          </p>
        </div>
      </CaseStudySection>

      {/* SECTION 5: KEY INSIGHTS */}
      <CaseStudySection id="insights" title="Key Insights" subtitle="What users and therapists really need." background="white" data-id="element-662">
        <div className="space-y-12" data-id="element-663">
          <p className="text-lg text-slate-600 leading-relaxed max-w-3xl" data-id="element-664">
            Three core insights shaped the design direction.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-665">
            {[{
            number: '01',
            persona: 'People in distress',
            challenge: "When emotions are high, people don't need advice — they need to feel heard and guided through the experience, not around it."
          }, {
            number: '02',
            persona: 'Long-term users',
            challenge: "Emotional growth is invisible in the moment. People lose motivation when they can't see their own progress or understand their patterns."
          }, {
            number: '03',
            persona: 'Therapists',
            challenge: "Therapists spend valuable session time catching up on the week's events, leaving less time for deep therapeutic work."
          }].map((insight, index) => <motion.div key={insight.number} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1,
            duration: 0.5
          }} className="relative bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-id="element-666">
                <div className="absolute -top-4 left-6" data-id="element-667">
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-500 text-white text-sm font-bold" data-id="element-668">
                    {insight.number}
                  </span>
                </div>
                <div className="pt-4" data-id="element-669">
                  <h4 className="text-lg font-bold text-slate-900 mb-3" data-id="element-670">
                    {insight.persona}
                  </h4>
                  <p className="text-slate-600 leading-relaxed" data-id="element-671">
                    {insight.challenge}
                  </p>
                </div>
              </motion.div>)}
          </div>

          <div className="border-l-4 border-blue-500 bg-blue-50/50 py-4 px-6 rounded-r max-w-3xl" data-id="element-672">
            <p className="text-slate-700 leading-relaxed" data-id="element-673">
              "I know something is wrong, but I can't figure out why. I just
              feel like I keep having the same feelings over and over."
            </p>
            <p className="text-sm text-slate-500 italic mt-2" data-id="element-674">
              — Beta user interview
            </p>
          </div>
        </div>
      </CaseStudySection>

      {/* SECTION 6: IDEATION */}
      <CaseStudySection id="ideation" background="gray" data-id="element-675">
        <div className="space-y-8" data-id="element-676">
          <h2 className="text-3xl font-bold text-slate-900 md:text-4xl lg:text-5xl" data-id="element-677">
            Ideation & Planning
            <motion.div initial={{
            width: 0
          }} whileInView={{
            width: '80px'
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3,
            duration: 0.8
          }} className="mt-4 h-1.5 bg-blue-500" data-id="element-678" />
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start" data-id="element-679">
            <div className="lg:col-span-3 space-y-6" data-id="element-680">
              <p className="text-xl text-slate-600 md:text-2xl leading-relaxed" data-id="element-681">
                Defining the visual metaphor and emotional system.
              </p>
              <div className="flex flex-wrap gap-2" data-id="element-682">
                {['Vision Workshops', 'Metaphor Development', 'Information Architecture', 'Prototype Sprints', 'Clinical Review Sessions'].map((activity, index) => <motion.span key={activity} initial={{
                opacity: 0,
                scale: 0.9
              }} whileInView={{
                opacity: 1,
                scale: 1
              }} viewport={{
                once: true
              }} transition={{
                delay: 0.4 + index * 0.05,
                duration: 0.3
              }} className="px-2 py-1 text-xs font-medium text-slate-600 bg-slate-100 rounded-full border border-slate-200" data-id="element-683">
                    {activity}
                  </motion.span>)}
              </div>

              <p className="text-lg text-slate-700 leading-relaxed" data-id="element-684">
                The central design challenge was finding a visual metaphor that
                could make the invisible visible. My goal was to turn a person's
                emotional history into something they could explore, understand,
                and feel ownership over. We workshopped extensively with the
                founding therapists to ensure every design decision was
                clinically sound.
              </p>
              <p className="text-lg text-slate-700 leading-relaxed" data-id="element-685">
                The "emotional topography" metaphor emerged as the organizing
                principle: a personal emotional landscape that grows richer and
                more detailed over time, giving users a way to navigate their
                inner world with the same curiosity they'd bring to exploring a
                new place.
              </p>

              <div className="border-l-4 border-blue-500 bg-blue-50/50 py-4 px-6 rounded-r" data-id="element-686">
                <p className="text-slate-700 leading-relaxed" data-id="element-687">
                  "What if your emotions weren't just things you felt — but
                  places you could visit and understand?"
                </p>
                <p className="text-sm text-slate-500 italic mt-2" data-id="element-688">
                  — Design workshop prompt
                </p>
              </div>
            </div>

            <div className="lg:col-span-2 overflow-hidden rounded-xl bg-slate-100 shadow-lg" data-id="element-689">
              <img src="https://cdn.magicpatterns.com/uploads/hBqHSAmAbTsAAyY1unvDEL/visual_metaphore.png" alt="Innermap emotional topography concept" className="w-full h-auto object-cover" data-id="element-690" />
              <div className="bg-white p-4 text-sm text-slate-500 border-t border-slate-100 italic" data-id="element-691">
                Exploring a variety of themes in nature, balancing depth,
                complexity, and clarity.
              </div>
            </div>
          </div>
        </div>
      </CaseStudySection>

      {/* SECTION 7: THE SOLUTION */}
      <CaseStudySection id="solution" title="The Solution" subtitle="A connected experience for therapists and their patients." activities={['Rapid Prototyping', 'Interaction Design', 'Data Visualization', 'Clinical Review', 'Beta Testing']} background="white" data-id="element-692">
        <div className="space-y-12" data-id="element-693">
          <p className="max-w-3xl text-lg text-slate-600 leading-relaxed" data-id="element-694">
            Over the course of a year, I prototyped three interconnected
            experiences — each designed to work together as a complete system
            for emotional growth and therapeutic support.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-695">
            {features.map((feature, index) => <motion.button key={feature.id} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1,
            duration: 0.5
          }} onClick={() => openFeature(feature)} className="group relative overflow-hidden rounded-2xl bg-slate-50 border border-slate-200 p-6 text-left transition-all hover:shadow-xl hover:scale-[1.02] hover:border-blue-300" data-id="element-696">
                <div className="relative mb-4 overflow-hidden rounded-xl bg-white shadow-md" data-id="element-697">
                  {!feature.image ? <div className="w-full h-48 bg-slate-100 flex flex-col items-center justify-center gap-2" data-id="element-698">
                      <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center" data-id="element-699">
                        <svg className="w-5 h-5 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-700">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" data-id="element-701" />
                        </svg>
                      </div>
                      <span className="text-sm font-medium text-slate-400" data-id="element-702">
                        Work in progress
                      </span>
                    </div> : <img src={feature.image} alt={feature.title} className="w-full h-48 object-cover object-center transition-transform duration-500 group-hover:scale-105" data-id="element-703" />}
                </div>

                {/* WIP Badge for items still in prototyping */}
                {feature.isWip && <div className="mb-3 inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 border border-amber-200 rounded-full" data-id="element-704">
                    <div className="w-1.5 h-1.5 rounded-full bg-amber-400" data-id="element-705" />
                    <span className="text-xs font-medium text-amber-700" data-id="element-706">
                      In prototyping — goals are aspirational
                    </span>
                  </div>}

                <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors" data-id="element-707">
                  {feature.title}
                </h3>
                <p className="text-slate-600 leading-relaxed text-sm" data-id="element-708">
                  {feature.description}
                </p>

                <div className="mt-4 flex items-center gap-1 text-sm font-medium text-blue-600" data-id="element-709">
                  <span data-id="element-710">Details</span>
                  <svg className="w-4 h-4 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-711">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" data-id="element-712" />
                  </svg>
                </div>

                <div className="absolute bottom-0 left-0 h-[3px] w-0 bg-blue-500 group-hover:w-full transition-all duration-500 ease-out" data-id="element-713" />
              </motion.button>)}
          </div>
        </div>
      </CaseStudySection>

      {/* Feature Modal */}
      <FeatureModal feature={selectedFeature} isOpen={isModalOpen} onClose={closeModal} onPrevious={goToPrevious} onNext={goToNext} hasPrevious={currentIndex > 0} hasNext={currentIndex < features.length - 1} data-id="element-714" />

      <Footer data-id="element-715" />
    </div>;
}